import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import {
  FiHome,
  FiList,
  FiMoreHorizontal,
  FiX,
} from "react-icons/fi";
import { emitFabMenuOpened, onFabMenuOpened } from "./fabMenuEvents.ts";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { usePreferencesStore } from "@/stores/usePreferencesStore";

type Destination = {
  label: string;
  path: "/accounts" | "/wrapped" | "/categories" | "/tags" | "/saving-pockets" | "/budget" | "/installments" | "/savings" | "/analytics" | "/profile";
};

export const MobileTabBar = () => {
  const { t } = useTranslation(["common", "nav"]);
  const location = useLocation();
  const navigate = useNavigate();
  const isAdvancedMode = usePreferencesStore((state) => state.appMode === "advanced");
  const [isOpen, setIsOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuItemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const isHome = location.pathname.startsWith("/dashboard");
  const isTransactions = location.pathname.startsWith("/transactions");

  const destinations: Destination[] = [
    {
      label: t("nav.accounts"),
      path: "/accounts",
    },
    ...(FEATURE_FLAGS.wrapped
      ? [{
          label: t("nav.wrapped"),
          path: "/wrapped" as const,
        }]
      : []),
    {
      label: t("nav.categories"),
      path: "/categories",
    },
    {
      label: t("nav.tags"),
      path: "/tags",
    },
    ...(isAdvancedMode
      ? [
          ...(FEATURE_FLAGS.savingPockets
            ? [{
                label: t("nav.savingPockets"),
                path: "/saving-pockets" as const,
              }]
            : []),
          {
            label: t("nav.budget"),
            path: "/budget" as const,
          },
          {
            label: t("nav.installments"),
            path: "/installments" as const,
          },
          {
            label: t("nav.savings"),
            path: "/savings" as const,
          },
          {
            label: t("nav.analytics"),
            path: "/analytics" as const,
          },
        ]
      : []),
    {
      label: t("nav.profile"),
      path: "/profile",
    },
  ];
  const isMore = destinations.some((destination) => location.pathname.startsWith(destination.path));

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(
    () =>
      onFabMenuOpened((openedMenuId) => {
        if (openedMenuId !== "navigation") setIsOpen(false);
      }),
    [],
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    menuItemRefs.current[0]?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const focused = document.activeElement;
        if (!menuRef.current?.contains(focused) && !moreButtonRef.current?.contains(focused)) return;
        setIsOpen(false);
        moreButtonRef.current?.focus();
        return;
      }
      const focused = document.activeElement;
      if (!menuRef.current?.contains(focused) && !moreButtonRef.current?.contains(focused)) return;
      const items = menuItemRefs.current.filter((item): item is HTMLButtonElement => Boolean(item));
      if (items.length === 0) return;
      if (event.key === "Home" || event.key === "End") {
        event.preventDefault();
        items[event.key === "Home" ? 0 : items.length - 1]?.focus();
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const current = items.indexOf(document.activeElement as HTMLButtonElement);
      const next = current < 0
        ? (event.key === "ArrowDown" ? 0 : items.length - 1)
        : (current + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
      event.preventDefault();
      items[next]?.focus();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const handleNavigate = (path: Destination["path"]) => {
    moreButtonRef.current?.focus();
    void navigate({ to: path, replace: false });
    setIsOpen(false);
  };

  const openMenu = () => {
    setIsOpen((current) => {
      const next = !current;
      if (next) emitFabMenuOpened("navigation");
      return next;
    });
  };

  return (
    <>
      {isOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 cursor-default bg-[color:color-mix(in_srgb,var(--ds-ground)_80%,transparent)] md:hidden"
          onClick={() => {
            setIsOpen(false);
            moreButtonRef.current?.focus();
          }}
          aria-label={t("common:actions.close")}
        />
      ) : null}

      {isOpen ? (
        <div
          ref={menuRef}
          id="mobile-navigation-menu"
          className="app-more-menu fixed bottom-[calc(env(safe-area-inset-bottom)+var(--app-space-4)+var(--app-fab-size)+var(--app-space-3))] left-[var(--app-offset-x)] z-60 w-[220px] max-h-[calc(100dvh-10rem-env(safe-area-inset-bottom))] overflow-y-auto rounded-[var(--ds-radius-lg)] border-[1.5px] border-[var(--ds-line)] bg-[var(--ds-surface-raised)] p-2 shadow-[var(--ds-shadow-2)] md:hidden"
          role="menu"
          aria-label={t("nav.more")}
        >
          {destinations.map((destination, index) => {
            const active = location.pathname.startsWith(destination.path);
            return (
              <button
                key={destination.path}
                ref={(element) => { menuItemRefs.current[index] = element; }}
                type="button"
                role="menuitem"
                onClick={() => handleNavigate(destination.path)}
                aria-current={active ? "page" : undefined}
                className={[
                  "app-focus-ring flex min-h-[46px] w-full items-center rounded-[var(--ds-radius-md)] px-3 text-left font-[var(--ds-font-display)] text-[22px] leading-[26px] tracking-[-0.02em]",
                  active ? "bg-[var(--ds-ground)]" : "hover:bg-[var(--ds-ground)]",
                ].join(" ")}
              >
                <span className="truncate">{destination.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      <nav
        className="app-mobile-nav fixed bottom-[calc(env(safe-area-inset-bottom)+var(--app-space-4))] left-[var(--app-offset-x)] z-50 flex items-center rounded-full border-[1.5px] border-[var(--ds-line)] bg-[var(--ds-surface-raised)] p-1 md:hidden"
        aria-label={t("common:navigation.primaryNavigation")}
      >
        <button
          type="button"
          onClick={() => void navigate({ to: "/dashboard", replace: false })}
          aria-label={t("nav.home")}
          aria-current={isHome ? "page" : undefined}
          className={[
            "app-focus-ring inline-flex h-12 w-12 items-center justify-center rounded-full transition-colors",
            isHome ? "bg-[var(--ds-ink)] text-[var(--ds-ground)]" : "text-[var(--ds-ink)] hover:bg-[var(--ds-ground)]",
          ].join(" ")}
        >
          <FiHome className="h-5 w-5" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => void navigate({ to: "/transactions", replace: false })}
          aria-label={t("nav.transactions")}
          aria-current={isTransactions ? "page" : undefined}
          className={[
            "app-focus-ring inline-flex h-12 w-12 items-center justify-center rounded-full transition-colors",
            isTransactions ? "bg-[var(--ds-ink)] text-[var(--ds-ground)]" : "text-[var(--ds-ink)] hover:bg-[var(--ds-ground)]",
          ].join(" ")}
        >
          <FiList className="h-5 w-5" aria-hidden="true" />
        </button>
        <button
          ref={moreButtonRef}
          type="button"
          onClick={openMenu}
          aria-label={t("nav.more")}
          aria-expanded={isOpen}
          aria-controls={isOpen ? "mobile-navigation-menu" : undefined}
          aria-haspopup="menu"
          aria-current={isMore ? "page" : undefined}
          className={[
            "app-focus-ring inline-flex h-12 w-12 items-center justify-center rounded-full transition-colors",
            isOpen || isMore ? "bg-[var(--ds-ink)] text-[var(--ds-ground)]" : "text-[var(--ds-ink)] hover:bg-[var(--ds-ground)]",
          ].join(" ")}
        >
          {isOpen ? <FiX className="h-5 w-5" aria-hidden="true" /> : <FiMoreHorizontal className="h-5 w-5" aria-hidden="true" />}
        </button>
      </nav>
    </>
  );
};
